import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { useOrganization } from "@/contexts/OrganizationContext";

export interface FormattedInfluencer {
  id: string;
  name: string;
  first_name: string;
  handle: string;
  email: string;
  phone: string;
  platform: string;
  avatar: string;
  profilePicUrl?: string | null;
  engagement: number;
  engagement_rate: string;
  reach: string;
  campaigns: number;
  avgFee: string;
  avgROI: string;
  location: string;
  isVerified: boolean;
  followers: string;
  followers_count: string;
  rate: number;
  tags: string[];
  brands: string[];
  thumbnails: string[];
  aiScore: number | null;
  confidence: string;
  tier: string | null;
  platforms: string[];
}

type FormattedInfluencerRpcRow =
  Database['public']['Functions']['get_all_influencers_formatted']['Returns'][number];

export function useAllInfluencers() {
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.id;

  return useQuery({
    queryKey: ['all-influencers', orgId],
    queryFn: async (): Promise<FormattedInfluencer[]> => {
      if (!orgId) return [];

      const { data, error } = await supabase.rpc('get_all_influencers_formatted', {
        p_org_id: orgId,
      });
      if (error) throw error;

      const rows = (data ?? []) as FormattedInfluencerRpcRow[];

      return rows.map((row) => {
        const id = row.id;
        const fullName = row.name || '';
        const firstName = fullName.split(' ')[0] || fullName;
        const engagementNum = Number(row.engagement) || 0;
        const followersRaw = row.followers || '';

        const rawAvatar = row.avatar || '';
        const rawThumbnails = (row.thumbnails as string[]) || [];
        const thumbnailPic =
          Array.isArray(rawThumbnails) && typeof rawThumbnails[0] === 'string'
            ? rawThumbnails[0]
            : '';

        let finalAvatar = '';
        if (
          thumbnailPic &&
          (thumbnailPic.startsWith('http') ||
            thumbnailPic.startsWith('/') ||
            thumbnailPic.startsWith('data:'))
        ) {
          finalAvatar = thumbnailPic;
        } else if (
          rawAvatar &&
          (rawAvatar.startsWith('http') ||
            rawAvatar.startsWith('/') ||
            rawAvatar.startsWith('data:'))
        ) {
          finalAvatar = rawAvatar;
        }

        return {
          id,
          name: fullName,
          first_name: firstName,
          handle: row.handle || '',
          email: row.email || '',
          phone: row.phone || '',
          platform: row.platform || 'Instagram',
          avatar: finalAvatar || rawAvatar || '??',
          profilePicUrl: finalAvatar || null,
          engagement: engagementNum,
          engagement_rate: engagementNum ? `${engagementNum.toFixed(2)}%` : '',
          reach: row.reach || '',
          campaigns: Number(row.campaigns) || 0,
          avgFee: row.avg_fee || '-',
          avgROI: row.avg_roi || '-',
          location: row.location || '',
          isVerified: Boolean(row.is_verified),
          followers: followersRaw,
          followers_count:
            row.followers_count != null
              ? String(row.followers_count)
              : followersRaw,
          rate: Number(row.rate) || 0,
          tags: row.tags || [],
          brands: [],
          thumbnails: rawThumbnails,
          aiScore: row.ai_score != null ? Number(row.ai_score) : null,
          confidence: 'High',
          tier: row.tier || null,
          platforms: [row.platform || 'Instagram'],
        };
      });
    },
    enabled: !!orgId,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}
